import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portfolioApi, type BallWith, type PracticeBand } from '../api/portfolioApi';
import type { NotificationCategory } from '../api/commandCenterApi';
import { commandCenterApi } from '../api/commandCenterApi';
import { portfolioKeys, commandCenterKeys } from '../queryKeys';

export function usePortfolio(params?: {
  search?: string;
  band?: PracticeBand;
  sort?: 'health' | 'spend' | 'name';
}) {
  return useQuery({
    queryKey: portfolioKeys.portfolio(params),
    queryFn: () => portfolioApi.getPortfolio(params),
    staleTime: 60_000,
  });
}

export function useMovers() {
  return useQuery({
    queryKey: portfolioKeys.movers(),
    queryFn: () => portfolioApi.getMovers(),
    staleTime: 5 * 60_000,
  });
}

export function useWorkQueue(params?: {
  ball?: BallWith;
  category?: NotificationCategory;
  practiceId?: string;
}) {
  return useQuery({
    queryKey: portfolioKeys.work(params),
    queryFn: () => portfolioApi.getWork(params),
    staleTime: 30_000,
  });
}

export function useWorkSummary() {
  return useQuery({
    queryKey: portfolioKeys.workSummary(),
    queryFn: () => portfolioApi.getWorkSummary(),
    staleTime: 30_000,
  });
}

export function usePracticePulse(practiceId: string | undefined) {
  return useQuery({
    queryKey: portfolioKeys.pulse(practiceId!),
    queryFn: () => portfolioApi.getPracticePulse(practiceId!),
    enabled: !!practiceId,
    staleTime: 60_000,
  });
}

/**
 * Resolve or snooze a work item. Invalidates both the new Today/Portfolio
 * queries and the older command-center ones, so the two surfaces cannot drift
 * apart while both exist.
 */
export function useResolveWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      update,
    }: {
      id: string;
      update: { status: 'done' | 'dismissed' } | { status: 'snoozed'; snoozedUntil: string };
    }) => commandCenterApi.updateNotification(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.all });
      queryClient.invalidateQueries({ queryKey: commandCenterKeys.all });
    },
  });
}
